import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, TrendingUp } from 'lucide-react'
import i18n from '@/i18n'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { getAvatarColor, getInitials } from '@/lib/utils/avatarUtils'
import type { PortfolioUpdate } from '@/lib/repositories'

interface PortfolioUpdateCardProps {
  update: PortfolioUpdate
  isAdmin?: boolean
  onImageClick?: (imageUrl: string) => void
  onDelete?: (id: string) => void
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function PortfolioUpdateCard({
  update,
  isAdmin = false,
  onImageClick,
  onDelete,
}: PortfolioUpdateCardProps) {
  const { t } = useTranslation()
  const timeAgo = useCallback(
    (dateStr: string): string => {
      const now = Date.now()
      const then = new Date(dateStr).getTime()
      const diffMs = now - then
      const diffMins = Math.floor(diffMs / 60000)
      if (diffMins < 1) return t('justNow')
      if (diffMins < 60) return t('minutesAgo', { count: diffMins })
      const diffHrs = Math.floor(diffMins / 60)
      if (diffHrs < 24) return t('hoursAgo', { count: diffHrs })
      const diffDays = Math.floor(diffHrs / 24)
      if (diffDays < 30) return t('daysAgo', { count: diffDays })
      const locale = i18n.language === 'zh' ? 'zh-CN' : i18n.language
      return new Date(dateStr).toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    },
    [t]
  )

  const displayName = update.displayName || update.username
  const initials = getInitials(displayName, update.username)
  const avatarColor = getAvatarColor(update.username)

  return (
    <Card className="border-white/[0.06] bg-neutral-950/60 backdrop-blur-sm hover:bg-neutral-900/60 hover:border-white/[0.1] transition-all duration-300 relative">
      {/* Admin delete */}
      {isAdmin && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete(update.id)
          }}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-neutral-600 hover:text-red-400 hover:bg-red-500/10 transition-colors z-10"
          title={t('deleteUpdate')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      <CardContent className="p-5 space-y-4">
        {/* Author + timestamp */}
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-9 h-9 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0',
              avatarColor
            )}
          >
            <span className="text-black text-xs font-bold">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-white text-sm font-medium">
              {displayName}
            </span>
            <span className="text-neutral-600 text-sm mx-1.5">·</span>
            <span className="text-neutral-500 text-xs">
              {timeAgo(update.createdAt)}
            </span>
          </div>
        </div>

        {/* Portfolio value */}
        {update.portfolioValue != null && (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-[11px] text-neutral-500 uppercase tracking-wider font-medium">
                {t('portfolioValue')}
              </p>
              <p className="text-xl font-bold text-emerald-400 tabular-nums tracking-tight">
                {formatCurrency(update.portfolioValue)}
              </p>
            </div>
          </div>
        )}

        {/* Screenshot */}
        {update.screenshotUrl && (
          <div
            className={cn(
              'rounded-xl overflow-hidden border border-white/[0.06]',
              onImageClick &&
                'cursor-pointer hover:border-white/[0.12] transition-colors'
            )}
            onClick={() => onImageClick?.(update.screenshotUrl!)}
          >
            <img
              src={update.screenshotUrl}
              alt={t('portfolioScreenshot')}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Positions */}
        {update.positions && (
          <div className="rounded-xl bg-neutral-900/50 border border-white/[0.04] p-4">
            <p className="text-[11px] text-neutral-500 uppercase tracking-wider font-medium mb-2">
              {t('positions')}
            </p>
            <pre className="text-neutral-300 text-sm font-mono leading-relaxed whitespace-pre-wrap">
              {update.positions}
            </pre>
          </div>
        )}

        {/* Commentary */}
        {update.commentary && (
          <p className="text-neutral-400 text-sm leading-relaxed italic border-l-2 border-emerald-500/30 pl-3">
            {update.commentary}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
